import { LoanOwner } from '~/Interfaces';
import { DropDownOption } from '~/components/shared/DropDown/DropDown';
import { CountryCode } from '~/enums';
import { Language } from '~/store/types/sharedTypes';
import { LoanAmountDefaultValues } from '~/constants/markets';
import { formatAmount } from './formatters.helper';

// This simple id generator is more than enough for our limited use of random ids in My Qred
export const generateId = () =>
  // Math.random should be unique because of its seeding algorithm.
  // Convert it to base 36 (numbers + letters), and grab the first 9 characters
  // after the decimal.
  `_${Math.random().toString(36).substr(2, 9)}`;

export const generateOwnerPlaceholder = (): LoanOwner => ({
  name: '',
  personalNumber: '',
  percentage: '',
  id: generateId(),
});

export const generateOfferAmounts = (
  minAmount: number,
  maxAmount: number,
  exchangeRate: number,
  market: CountryCode
): Array<DropDownOption> => {
  let amount = minAmount || LoanAmountDefaultValues[market].min;
  const options = [
    {
      label: formatAmount(amount),
      value: String(amount),
    },
  ];
  if (amount === maxAmount) {
    return options;
  }
  const smallStep = 5000 * exchangeRate;
  const largeStep = 10000 * exchangeRate;
  const largeStepBreakpoint = 100000 * exchangeRate;

  let step = amount >= largeStepBreakpoint ? largeStep : smallStep;
  // amount = Math.ceil(amount / step) * step; // we should use this to get an even new loan amount
  amount += step; // we should use this to get an even payout
  while (amount < maxAmount) {
    options.push({
      label: formatAmount(amount),
      value: String(amount),
    });
    if (step === smallStep && amount >= largeStepBreakpoint) {
      step = largeStep;
    }
    amount += step;
  }

  options.push({
    label: formatAmount(maxAmount),
    value: String(maxAmount),
  });

  return options;
};

// Split language to ignore market in case langauge contains both variables. (For example NL_BE).
// Format of locale: language_MARKET
export const generateLocale = (language: Language, market: CountryCode) =>
  `${language.split('_')[0].toLowerCase()}-${market.toUpperCase()}`;
